import { LuiIcon } from '@linzjs/lui';
import { LuiIconName } from '@linzjs/lui/dist/assets/svg-content';
import clsx from 'clsx';
import { ReactElement, useContext, useState } from 'react';

import { PanelContext } from './PanelContext';
import { PanelHeaderButton } from './PanelHeaderButton';
import { PanelInstanceContext } from './PanelInstanceContext';

export interface PanelHeaderProps {
  icon?: LuiIconName;
  extraLeft?: ReactElement;
  extraRight?: ReactElement;
  helpUrl?: string;
  onHelpClick?: () => void;
  onDockClick?: () => void;
  // Popped out windows are native so will not fire this, if you use this make the panel non popout!
  // Return true to close the panel, otherwise false to keep it open.
  onCloseClickPoppedInPanelOnly?: () => Promise<boolean> | boolean;
  dockTo?: string;
  disableClose?: boolean;
  disablePopout?: boolean;
}

export const PanelHeader = ({
  icon,
  extraLeft,
  extraRight,
  helpUrl,
  onHelpClick,
  onDockClick,
  onCloseClickPoppedInPanelOnly,
  dockTo,
  disableClose,
  disablePopout,
}: PanelHeaderProps) => {
  const { resizeable } = useContext(PanelContext);
  const { panelClose, panelTogglePopout, panelPoppedOut, title, dock, undock, docked } =
    useContext(PanelInstanceContext);
  const [cursor, setCursor] = useState<'grab' | 'grabbing'>('grab');

  const headerMouseDown = () => {
    if (!panelPoppedOut) {
      setCursor('grabbing');
    }
  };

  return (
    <div
      className={clsx('WindowPanel-header', resizeable && 'draggable-handle')}
      onMouseDown={headerMouseDown}
      onMouseUp={() => !panelPoppedOut && setCursor('grab')}
      style={resizeable ? { cursor } : {}}
    >
      <div className={'WindowPanel-header-title'}>
        {icon && <LuiIcon name={icon} alt={'icon'} size={'md'} className={'WindowPanel-header-title-icon'} />}
        {title}
      </div>
      {extraLeft && <div className={'WindowPanel-divider-left'} />}
      <div className={'WindowPanel-children'}>{extraLeft}</div>
      <div className={'WindowPanel-buttons'}>
        {extraRight}
        {(helpUrl || onHelpClick) && (
          <PanelHeaderButton aria-label={'Get help'} href={helpUrl} onClick={onHelpClick} icon={'ic_help_outline'} />
        )}
      </div>
      {(dockTo || !disablePopout || !disableClose) && <div className={'WindowPanel-divider-right'} />}
      <div className={'WindowPanel-buttons'}>
        {onDockClick && <PanelHeaderButton aria-label={'Dock'} onClick={() => onDockClick()} icon={'ic_left_col'} />}
        {!onDockClick &&
          dockTo &&
          (docked ? (
            <PanelHeaderButton aria-label={'Undock'} onClick={undock} icon={'ic_close_list'} />
          ) : (
            <PanelHeaderButton aria-label={'Dock'} onClick={() => dock(dockTo)} icon={'ic_left_col'} />
          ))}
        {!disablePopout && (
          <PanelHeaderButton
            aria-label={panelPoppedOut ? 'Pop-in' : 'Pop-out'}
            onClick={panelTogglePopout}
            icon={panelPoppedOut ? 'ic_pop_back' : 'ic_launch_new window_open'}
          />
        )}
        {!disableClose && (
          <PanelHeaderButton
            aria-label={'Close'}
            onClick={() => {
              if (onCloseClickPoppedInPanelOnly) {
                void (async () => {
                  const result = await onCloseClickPoppedInPanelOnly();
                  if (result) {
                    panelClose();
                  }
                })();
              } else {
                panelClose();
              }
            }}
            icon={'ic_clear'}
          />
        )}
      </div>
    </div>
  );
};
